"use client";

import { FC, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CancelIcon from "@mui/icons-material/Cancel";
import InputAdornment from "@mui/material/InputAdornment";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import SubmitIcon from "@/components/icons/Send";

interface CommentFormProps {
  handleSubmit: (text: string, parentId: Nullable<number>) => void;
  hasCancelButton?: boolean;
  initialText?: string;
  handleCancel?: () => void;
  currentUserId: Nullable<number>;
}

const CommentForm: FC<CommentFormProps> = ({
  handleSubmit,
  hasCancelButton = false,
  initialText = "",
  handleCancel,
  currentUserId,
}) => {
  const { classes } = useStyles();
  const [text, setText] = useState(initialText);
  const [inputDisabled, setInputDisabled] = useState(Boolean(currentUserId));
  const [placeholderText, setPlaceholderText] = useState(
    hasCancelButton ? "Add a reply..." : "Add a comment..."
  );
  const isDisabled = text.length === 0;

  const onSubmit = (event: any) => {
    event.preventDefault();
    handleSubmit(text, null);
    setText("");
  };

  useEffect(() => {
    if (!currentUserId) {
      setPlaceholderText("Log in to comment");
      setInputDisabled(true);
    } else {
      setInputDisabled(false);
      setPlaceholderText(
        hasCancelButton ? "Add a reply..." : "Add a comment..."
      );
    }
  }, [currentUserId]);

  return (
    <form className={classes.form} onSubmit={onSubmit}>
      <TextField
        disabled={inputDisabled}
        placeholder={placeholderText}
        value={text}
        onChange={(e) => {
          if (e.target.value.length <= 250) {
            setText(e.target.value);
          }
        }}
        variant="outlined"
        helperText={`${text.length}/250`}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                className={classes.icon}
                onClick={onSubmit}
                disabled={isDisabled}
                aria-label="submit comment"
              >
                <SubmitIcon />
              </IconButton>
              {hasCancelButton && (
                <IconButton onClick={handleCancel} aria-label="cancel">
                  <CancelIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        className={classes.textField}
        error={text.length > 250}
      />
    </form>
  );
};

export default CommentForm;
